import Link from 'next/link'
import React from 'react'

import { useStoriesDetail } from '@/shared/api/generated/stories/stories'
import {
  StyledItemInfoColumn, StyledItemName, StyledLineGrid, StyledLineImage, StyledNextImage,
  StyledOrderLineButton, StyledOrderLineButtonsContainer, StyledOrderLineContent,
  StyledOrderLineImage, StyledPriceColumn, StyledPriceTypography,
} from '@/shared/components/CheckoutCart/CheckoutCartLine'
import { HardcoverMockup, orientationFromAspectRatio } from '@/shared/components/HardcoverMockup'
import { Icon } from '@/shared/components/Icon/Icon'
import { formatUsd } from '@/shared/libs/money'
import { pxToRem } from '@/shared/styles/themes'
import { faMinus } from '@fortawesome/pro-light-svg-icons/faMinus'
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { Alert, Box, Grid2 as Grid, IconButton, Skeleton, styled, Typography } from '@mui/material'

import type { CartQuoteLine } from '@/shared/api/models'
import type { CartLine, CartLineKind } from '../CartContext'

const PRODUCT_IMAGES: Record<CartLineKind, string> = {
  software: '/images/products/pixie_world_software_package.png',
  unlimited: '/images/products/unlimited_storybooks.png',
  credit_pack: '/images/products/1000_credit_packs.png',
  physical_book: '/images/products/hardcover_sample.png',
}

const SOFTCOVER_IMAGE = '/images/products/softcover_sample.png'

const FALLBACK_NAMES: Record<CartLineKind, string> = {
  software: 'Single Story',
  unlimited: 'Unlimited Stories',
  credit_pack: 'Credit Pack',
  physical_book: 'Printed Storybook',
}

const MAX_BOOK_QUANTITY = 10

function getProductImage(line: CartLine, quoteLine?: CartQuoteLine): string {
  if (line.kind === 'physical_book' && /softcover/i.test(quoteLine?.name ?? '')) {
    return SOFTCOVER_IMAGE
  }

  return PRODUCT_IMAGES[line.kind]
}

function getSubtitle(line: CartLine, quoteLine?: CartQuoteLine): string | undefined {
  const storyTitle = quoteLine?.story_title

  if (line.kind === 'software') {
    return storyTitle ? `Unlocks "${storyTitle}" + credits for your next story` : quoteLine?.description || undefined
  }

  if (line.kind === 'unlimited') {
    return storyTitle
      ? `Unlocks "${storyTitle}" + unlimited story creation`
      : 'Unlimited story creation (fair use: 50 stories/month)'
  }

  if (line.kind === 'credit_pack') {
    return quoteLine?.description || 'Use credits to generate new stories and illustrations'
  }

  return storyTitle ? `Printed copy of "${storyTitle}"` : 'Can be used to print any finished story'
}

function getHref(line: CartLine, quoteLine?: CartQuoteLine): string | undefined {
  // Story-bound lines (unlock or printed copy) link to the story itself.
  if (line.story_id) return `/library/${line.story_id}`
  if (line.kind === 'credit_pack' && line.plan_id) return `/shop/credit-packs/${line.plan_id}`

  if (line.kind === 'physical_book') {
    return /softcover/i.test(quoteLine?.name ?? '') ? '/shop/softcover-book' : '/shop/hardcover-book'
  }

  // Standalone plans have no product page of their own (/shop/software redirects home).
  return undefined
}

interface CartLineRowProps {
  line: CartLine
  /** Matching quote line (same key); undefined while the first quote loads */
  quoteLine?: CartQuoteLine
  isPricing: boolean
  onRemove: () => void
  onQuantityChange: (quantity: number) => void
}

/**
 * One cart line. Prices come from the server quote; books get a quantity
 * stepper, and lines tied to a story show the story's cover.
 */
export const CartLineRow: React.FC<CartLineRowProps> = ({
  line, quoteLine, isPricing, onRemove, onQuantityChange,
}) => {
  const { data: storyData } = useStoriesDetail(line.story_id ?? 0, {
    query: { enabled: !!line.story_id },
  })
  const story = storyData?.story
  const coverUrl = story?.cover_image_url || quoteLine?.image_url || null

  const name = quoteLine?.name || FALLBACK_NAMES[line.kind]
  const href = getHref(line, quoteLine)
  const productImage = getProductImage(line, quoteLine)
  const isBook = line.kind === 'physical_book'

  // Books tied to a story render the cover inside the hardcover mockup;
  // story-unlock lines show the product art with the cover as a badge.
  const widthMm = story?.print_dimensions?.page_width_mm
  const heightMm = story?.print_dimensions?.page_height_mm
  const orientation = widthMm && heightMm ? orientationFromAspectRatio(widthMm / heightMm) : 'square'

  const image = (
    <StyledLineImage>
      {isBook && coverUrl ? (
        <StyledMockupWrapper>
          <HardcoverMockup coverImageUrl={coverUrl} orientation={orientation} alt={`${name} preview`} />
        </StyledMockupWrapper>
      ) : (
        <StyledNextImage src={productImage} alt={name} fill unoptimized style={{ objectFit: 'cover' }} />
      )}
      {!isBook && coverUrl && (
        <StyledCoverBadge src={coverUrl} alt="" />
      )}
    </StyledLineImage>
  )

  const unitPrice = quoteLine ? parseFloat(quoteLine.unit_price) : null
  const compareAt = quoteLine?.compare_at_price ? parseFloat(quoteLine.compare_at_price) : null

  return (
    <StyledLineGrid container direction="row" alignItems="stretch" justifyContent="flex-start" wrap="nowrap" columns={15}>
      <StyledOrderLineImage size={{ xs: 4, sm: 'auto' }}>
        {href ? <Link href={href}>{image}</Link> : image}
      </StyledOrderLineImage>
      <StyledOrderLineContent>
        <Grid container direction="row" justifyContent="space-between" flexWrap="wrap">
          <StyledItemInfoColumn>
            {href ? (
              <Link href={href}>
                <StyledItemName variant="body1" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                  {name}
                </StyledItemName>
              </Link>
            ) : (
              <StyledItemName variant="body1">{name}</StyledItemName>
            )}
            <Typography variant="body2" color="text.secondary">
              {getSubtitle(line, quoteLine)}
            </Typography>
            {isBook && quoteLine && line.quantity > 1 && (
              <Typography variant="body2" color="text.secondary">
                {formatUsd(quoteLine.unit_price)} each
              </Typography>
            )}
          </StyledItemInfoColumn>
          <StyledPriceColumn>
            {!quoteLine ? (
              isPricing ? <Skeleton variant="text" width={60} sx={{ ml: 'auto' }} /> : null
            ) : quoteLine.error ? null : (
              <>
                {compareAt !== null && unitPrice !== null && compareAt > unitPrice && (
                  <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {formatUsd(compareAt * line.quantity)}
                  </Typography>
                )}
                <StyledPriceTypography variant="body2">
                  {formatUsd(quoteLine.line_total)}
                </StyledPriceTypography>
              </>
            )}
          </StyledPriceColumn>
        </Grid>

        {quoteLine?.error && (
          <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
            {quoteLine.error}
          </Alert>
        )}

        <StyledOrderLineButtonsContainer>
          {isBook && (
            <StyledStepper>
              <IconButton
                size="small"
                aria-label="Decrease quantity"
                disabled={line.quantity <= 1}
                onClick={() => onQuantityChange(line.quantity - 1)}
              >
                <Icon icon={faMinus} sx={{ fontSize: pxToRem(12) }} />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: pxToRem(20), textAlign: 'center' }} aria-live="polite">
                {line.quantity}
              </Typography>
              <IconButton
                size="small"
                aria-label="Increase quantity"
                disabled={line.quantity >= MAX_BOOK_QUANTITY}
                onClick={() => onQuantityChange(line.quantity + 1)}
              >
                <Icon icon={faPlus} sx={{ fontSize: pxToRem(12) }} />
              </IconButton>
            </StyledStepper>
          )}
          <StyledOrderLineButton text round onClick={onRemove} startIcon={<Icon icon={faTrash} sx={{ fontSize: pxToRem(14) }} />}>
            Remove
          </StyledOrderLineButton>
        </StyledOrderLineButtonsContainer>
      </StyledOrderLineContent>
    </StyledLineGrid>
  )
}

const StyledMockupWrapper = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${pxToRem(12)};
  overflow: hidden;

  & > * {
    max-width: 100%;
    max-height: 100%;
  }
`

const StyledCoverBadge = styled('img')`
  position: absolute;
  bottom: ${pxToRem(6)};
  right: ${pxToRem(6)};
  width: 40%;
  height: 40%;
  object-fit: cover;
  border-radius: ${pxToRem(6)};
  border: 2px solid ${props => props.theme.palette.background.paper};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
`

const StyledStepper = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: ${pxToRem(4)};
  margin-left: ${pxToRem(12)};
  border: 1px solid ${props => props.theme.palette.divider};
  border-radius: ${pxToRem(20)};
  padding: 0 ${pxToRem(4)};
`
